import React, { useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useChatbotThreadMessagesQuery } from '../../common/queries/use-chatbot-query';
import { AssistantAvatar } from '../../screens/assistant/assistant-avatar';
import MessageBubble from './MessageBubble';
import PlaceholderIndicator from './PlaceholderIndicator';
import ChatSuggestions from './ChatSuggestions';
import { Suggestion } from '../../models/chatbot/types';

interface ChatMessagesProps {
  threadId?: string;
  isThreadPersisted?: boolean;
  programId: string;
  suggestions?: Suggestion[];
  onSuggestionClick?: (question: string) => void;
}

const ChatMessages: React.FC<ChatMessagesProps> = ({
  threadId,
  isThreadPersisted = false,
  programId,
  suggestions = [],
  onSuggestionClick,
}) => {
  const { t } = useTranslation();
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const lastScrolledMessageId = useRef<string | null>(null);

  const { data: messages, isLoading } = useChatbotThreadMessagesQuery(
    programId,
    threadId,
    { enabled: !!threadId && !!programId && isThreadPersisted }
  );

  const displayMessages = useMemo(() => messages || [], [messages]);

  // Scroll to the start of new messages (not bottom) when they first appear
  useEffect(() => {
    if (displayMessages.length === 0) return;

    const lastMessage = displayMessages[displayMessages.length - 1];
    if (!lastMessage) return;

    // Only scroll when a new message appears, not during streaming updates
    if (lastMessage.id !== lastScrolledMessageId.current) {
      lastScrolledMessageId.current = lastMessage.id;

      const messageElement = messageRefs.current.get(lastMessage.id);
      if (messageElement) {
        messageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [displayMessages]);

  if (isLoading && threadId && isThreadPersisted) {
    return (
      <div className="chatbot__loading chatbot__loading--centered">
        <div className="chatbot__loading-content">
          <div className="spinner"></div>
          <div className="chatbot__loading-text">{t('chatbot.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="chatbot__messages">
      <div className="chatbot__messages-inner">
        <div
          className="chatbot__messages-content"
          role="log"
          aria-live="polite"
          aria-label={t('chatbot.conversation')}
        >
          {displayMessages.length === 0 ? (
            // Show empty state with suggestions at bottom
            <>
              <div className="chatbot__empty-state">
                <div className="chatbot__empty-state-icon">
                  <AssistantAvatar size={48} />
                </div>
                <h3 className="chatbot__empty-state-title">
                  {t('chatbot.emptyState.title')}
                </h3>
                <p className="chatbot__empty-state-description">
                  {t('chatbot.emptyState.description')}
                </p>
              </div>

              {/* Show suggestions near the bottom if available */}
              {suggestions.length > 0 && onSuggestionClick && (
                <div className="chatbot__suggestions-wrapper">
                  <ChatSuggestions
                    suggestions={suggestions}
                    onSuggestionClick={onSuggestionClick}
                  />
                </div>
              )}
            </>
          ) : (
            // Show actual messages
            displayMessages.map((message) => {
              // Hide message bubble if assistant has no content yet (while placeholder is showing)
              const shouldShowBubble =
                message.role === 'user' ||
                (message.role === 'assistant' && message.content);

              return (
                <div
                  key={message.id}
                  ref={(el) => {
                    if (el) {
                      messageRefs.current.set(message.id, el);
                    }
                  }}
                >
                  {/* Show placeholder before assistant messages when available */}
                  {message.role === 'assistant' && message.placeholderText && (
                    <PlaceholderIndicator text={message.placeholderText} />
                  )}

                  {shouldShowBubble && (
                    <MessageBubble
                      message={message}
                      isStreaming={message.isStreaming || false}
                    />
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatMessages;
